import React, { useContext, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    CircularProgress,
    useMediaQuery,
} from "@mui/material";

import {
    Email,
    Lock,
    Visibility,
    VisibilityOff,
    ArrowBack,
    VerifiedUser,
    Engineering,
    Construction,
    Build,
    Bolt,
    PrecisionManufacturing,
    Handyman,
    ElectricalServices,
    Plumbing,
    Carpenter,
    Settings,
    ArrowForward,
    School,
} from "@mui/icons-material";

import api from "../api/api";
import { AuthContext } from "../AuthContext";

import student from "./schoolmanagementsystem.png";

const COLORS = {
    navy: "#071923",
    deepNavy: "#041017",
    charcoal: "#0D2028",
    steel: "#18343F",
    green: "#16834B",
    brightGreen: "#22A861",
    gold: "#E5A91A",
    brightGold: "#F4C542",
    white: "#FFFFFF",
    cream: "#F7F8F5",
    text: "#12252D",
    muted: "#6B7A80",
    border: "#DCE5E5",
    paleGreen: "#EEF8F2",
    danger: "#B42318",
};

const FloatingTradeIcon = ({
    Icon,
    top,
    left,
    right,
    bottom,
    delay = 0,
    size = 28,
}) => (
    <motion.div
        initial={{
            opacity: 0,
            y: 30,
            scale: 0.7,
        }}
        animate={{
            opacity: [0, 0.75, 0.45, 0],
            y: [25, -5, -30, -65],
            x: [0, 10, -5, 15],
            rotate: [0, 12, -8, 15],
            scale: [0.7, 1, 1.05, 0.65],
        }}
        transition={{
            duration: 8,
            delay,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            top,
            left,
            right,
            bottom,
            zIndex: 5,
            pointerEvents: "none",
        }}
    >
        <Icon
            sx={{
                fontSize: size,
                color: COLORS.brightGold,
                opacity: 0.65,
                filter:
                    "drop-shadow(0 0 12px rgba(244,197,66,.25))",
            }}
        />
    </motion.div>
);

const Glow = ({
    size = 400,
    top,
    left,
    right,
    bottom,
    color = "rgba(34,168,97,.18)",
}) => (
    <motion.div
        animate={{
            scale: [0.92, 1.08, 0.92],
            opacity: [0.35, 0.65, 0.35],
        }}
        transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
        }}
        style={{
            position: "absolute",
            width: size,
            height: size,
            top,
            left,
            right,
            bottom,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
            pointerEvents: "none",
            zIndex: 1,
        }}
    />
);

const TechnicalGrid = () => (
    <Box
        sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.08,
            pointerEvents: "none",
            backgroundImage: `
                linear-gradient(rgba(255,255,255,.55) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,.55) 1px, transparent 1px)
            `,
            backgroundSize: "42px 42px",
            maskImage:
                "linear-gradient(to bottom, black, transparent 85%)",
        }}
    />
);

const Login = () => {
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);

    const isMobile = useMediaQuery("(max-width:900px)");

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const redirectUser = (user) => {
        const role = String(
            user?.user_type || user?.role || ""
        )
            .toLowerCase()
            .trim();

        switch (role) {
            case "owner":
                navigate("/owner-dashboard");
                break;
            case "admin":
                navigate("/newAdminDashboard");
                break;
            case "teacher":
                navigate("/teacher-dashboard");
                break;
            case "student":
                navigate("/student-dashboard");
                break;
            case "parent":
                navigate("/parentsDashboard");
                break;
            case "registry":
                navigate("/registerDashboard");
                break;
            case "principal":
                navigate("/principal-dashboard");
                break;
            case "proprietor":
                navigate("/proprietor-dashboard");
                break;
            case "vpi":
                navigate("/vpi-dashboard");
                break;
            case "vpa":
                navigate("/newVpaDashboard");
                break;
            case "it personel":
            case "it personnel":
                navigate("/it-dashboard");
                break;
            case "business_manager":
            case "business manager":
                navigate("/businessManagerDashboard");
                break;
            case "admission officer":
            case "admission_officer":
                navigate("/admissionOfficialDashboard");
                break;
            case "dean":
                navigate("/dean-dashboard");
                break;
            default:
                navigate("/");
        }
    };

    const clearAuthenticationData = () => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        localStorage.removeItem("user");
        localStorage.removeItem("employment_status");
        localStorage.removeItem("employment_status_display");
        localStorage.removeItem("employment_academic_year");
        localStorage.removeItem("auth_email");
    };

    const getLoginError = (error) => {
        const responseData = error?.response?.data;

        if (responseData?.detail) {
            return String(responseData.detail);
        }

        if (responseData?.message) {
            return String(responseData.message);
        }

        if (typeof responseData === "string") {
            return responseData;
        }

        if (
            responseData &&
            typeof responseData === "object"
        ) {
            const messages = [];

            Object.entries(responseData).forEach(
                ([field, value]) => {
                    if (field === "detail") {
                        return;
                    }

                    if (Array.isArray(value)) {
                        value.forEach((item) => {
                            if (
                                item !== null &&
                                item !== undefined
                            ) {
                                messages.push(String(item));
                            }
                        });
                    } else if (
                        typeof value === "string"
                    ) {
                        messages.push(value);
                    }
                }
            );

            if (messages.length) {
                return messages.join("\n");
            }
        }

        return "Invalid email or password.";
    };

    const showEmploymentStatusAlert = (data) => {
        const errorCode = String(
            data?.error_code || ""
        )
            .toUpperCase()
            .trim();

        const status = String(
            data?.employment_status || ""
        )
            .toLowerCase()
            .trim();

        const academicYear =
            data?.academic_year ||
            data?.academic_year_name ||
            "the current academic year";

        const statusDisplay =
            data?.employment_status_display ||
            (status === "fired"
                ? "SACKED / FIRED"
                : status === "removed"
                    ? "REMOVED"
                    : "");

        if (
            errorCode === "TEACHER_FIRED" ||
            errorCode === "EMPLOYMENT_FIRED" ||
            status === "fired"
        ) {
            Swal.fire({
                icon: "error",
                title: "Account Blocked!",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account cannot be used to access
                            the training institute system.
                        </p>

                        <div style="
                            display:inline-block;
                            padding:8px 16px;
                            border-radius:20px;
                            background:#FEF2F2;
                            color:#B91C1C;
                            font-weight:800;
                            margin:10px 0 14px;
                        ">
                            EMPLOYMENT STATUS:
                            ${statusDisplay || "SACKED / FIRED"}
                        </div>

                        <p style="color:#6B7280;line-height:1.6;">
                            Your most recent employment record
                            indicates that your employment was
                            terminated.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized institute
                            administrator for assistance.
                        </p>

                        <p style="
                            color:#6B7280;
                            font-size:13px;
                            margin-top:12px;
                        ">
                            Latest employment record:
                            <strong>${academicYear}</strong>
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.green,
            });

            return true;
        }

        if (
            errorCode === "TEACHER_REMOVED" ||
            errorCode === "EMPLOYMENT_REMOVED" ||
            status === "removed"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Account Blocked!",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account cannot be used to access
                            the training institute system.
                        </p>

                        <div style="
                            display:inline-block;
                            padding:8px 16px;
                            border-radius:20px;
                            background:#F0FDF4;
                            color:#166534;
                            font-weight:800;
                            margin:10px 0 14px;
                        ">
                            EMPLOYMENT STATUS:
                            ${statusDisplay || "REMOVED"}
                        </div>

                        <p style="color:#6B7280;line-height:1.6;">
                            Your most recent employment record
                            indicates that you have been removed
                            from the institute.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized institute
                            administrator for assistance.
                        </p>

                        <p style="
                            color:#6B7280;
                            font-size:13px;
                            margin-top:12px;
                        ">
                            Latest employment record:
                            <strong>${academicYear}</strong>
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.green,
            });

            return true;
        }

        if (errorCode === "TEACHER_NOT_ASSIGNED") {
            Swal.fire({
                icon: "warning",
                title: "Instructor Not Assigned",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your instructor account is not currently
                            assigned to an active training year.
                        </p>

                        <p style="
                            font-size:17px;
                            font-weight:800;
                            color:${COLORS.green};
                        ">
                            ${academicYear}
                        </p>

                        <p style="color:#6B7280;line-height:1.6;">
                            Please contact institute administration
                            to have your instructor record assigned.
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.green,
            });

            return true;
        }

        if (errorCode === "NO_ACTIVE_ACADEMIC_YEAR") {
            Swal.fire({
                icon: "warning",
                title: "No Active Training Year",
                text:
                    "There is currently no active training year. Please contact the institute administration.",
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.green,
            });

            return true;
        }

        if (
            errorCode === "EMPLOYMENT_LOGIN_BLOCKED" ||
            errorCode === "LOGIN_BLOCKED"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Login Blocked",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account is currently blocked from
                            accessing the training institute system.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized institute
                            administrator.
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.green,
            });

            return true;
        }

        return false;
    };

    const handleBlockedResponse = (data) => {
        if (!data) {
            return false;
        }

        const status = String(
            data?.employment_status || ""
        )
            .toLowerCase()
            .trim();

        const errorCode = String(
            data?.error_code || ""
        )
            .toUpperCase()
            .trim();

        const employmentCodes = [
            "TEACHER_REMOVED",
            "TEACHER_FIRED",
            "EMPLOYMENT_REMOVED",
            "EMPLOYMENT_FIRED",
            "EMPLOYMENT_LOGIN_BLOCKED",
            "LOGIN_BLOCKED",
            "TEACHER_NOT_ASSIGNED",
            "NO_ACTIVE_ACADEMIC_YEAR",
        ];

        const isEmploymentError =
            employmentCodes.includes(errorCode) ||
            ["removed", "fired"].includes(status);

        if (!isEmploymentError) {
            return false;
        }

        clearAuthenticationData();

        return showEmploymentStatusAlert(data);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanEmail = email.trim();

        if (!cleanEmail || !password) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text:
                    "Please enter your email address and password.",
                confirmButtonColor: COLORS.green,
            });

            return;
        }

        setLoading(true);

        try {
            clearAuthenticationData();

            const response = await api.post(
                "/api/token/",
                {
                    email: cleanEmail,
                    password,
                }
            );

            console.log(
                "TOKEN RESPONSE:",
                response.data
            );

            if (
                handleBlockedResponse(
                    response.data
                )
            ) {
                return;
            }

            const role = String(
                response.data?.user_type ||
                response.data?.user?.role ||
                ""
            )
                .toLowerCase()
                .trim();

            const authenticationRoles = [
                "admin",
                "registry",
                "principal",
                "proprietor",
                "business_manager",
                "business manager",
                "vpi",
                "vpa",
                "it personel",
                "it personnel",
            ];

            if (
                response.data?.requires_authentication &&
                authenticationRoles.includes(role)
            ) {
                const authEmail =
                    response.data?.email ||
                    response.data?.user?.email ||
                    cleanEmail;

                localStorage.setItem(
                    "auth_email",
                    authEmail
                );

                navigate(
                    "/loginAuthentication",
                    {
                        state: {
                            email: authEmail,
                        },
                    }
                );

                return;
            }

            const access =
                response.data?.access;

            const refresh =
                response.data?.refresh;

            if (!access || !refresh) {
                throw new Error(
                    "Authentication tokens were not returned."
                );
            }

            localStorage.setItem(
                "access",
                access
            );

            localStorage.setItem(
                "refresh",
                refresh
            );

            if (
                response.data?.employment_status
            ) {
                localStorage.setItem(
                    "employment_status",
                    response.data.employment_status
                );
            }

            if (
                response.data?.employment_status_display
            ) {
                localStorage.setItem(
                    "employment_status_display",
                    response.data
                        .employment_status_display
                );
            }

            if (
                response.data?.academic_year
            ) {
                localStorage.setItem(
                    "employment_academic_year",
                    response.data.academic_year
                );
            }

            window.dispatchEvent(
                new Event("user-logged-in")
            );

            let profileResponse;

            try {
                profileResponse =
                    await api.get(
                        "/api/user-profile/"
                    );
            } catch (profileError) {
                clearAuthenticationData();
                throw profileError;
            }

            const user =
                profileResponse.data;

            const frontendStatus = String(
                response.data
                    ?.employment_status ||
                user?.employment_status ||
                ""
            )
                .toLowerCase()
                .trim();

            if (
                frontendStatus === "removed" ||
                frontendStatus === "fired"
            ) {
                clearAuthenticationData();

                showEmploymentStatusAlert({
                    error_code:
                        frontendStatus === "removed"
                            ? "TEACHER_REMOVED"
                            : "TEACHER_FIRED",
                    employment_status:
                        frontendStatus,
                    employment_status_display:
                        frontendStatus === "removed"
                            ? "REMOVED"
                            : "SACKED / FIRED",
                    academic_year:
                        response.data?.academic_year ||
                        response.data
                            ?.academic_year_name,
                });

                return;
            }

            setUser(user);

            localStorage.setItem(
                "user",
                JSON.stringify(user)
            );

            Swal.fire({
                icon: "success",
                title: "Welcome Back!",
                text: `Welcome ${
                    user?.user_type ||
                    user?.role ||
                    ""
                }`,
                timer: 1800,
                showConfirmButton: false,
                background: COLORS.white,
                color: COLORS.black,
                iconColor: COLORS.green,
            });

            setTimeout(() => {
                redirectUser(user);
            }, 1700);
        } catch (error) {
            console.error(
                "LOGIN ERROR:",
                error
            );

            const responseData =
                error?.response?.data;

            if (responseData) {
                const handled =
                    handleBlockedResponse(
                        responseData
                    );

                if (handled) {
                    return;
                }
            }

            Swal.fire({
                icon: "error",
                title: "Login Failed",
                text: getLoginError(error),
                confirmButtonText: "Try Again",
                confirmButtonColor: COLORS.green,
                background: COLORS.white,
                color: COLORS.black,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                width: "100%",
                background: COLORS.cream,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: {
                        xs: "block",
                        md: "grid",
                    },
                    gridTemplateColumns: {
                        md: "1.15fr .85fr",
                    },
                }}
            >
                {/* =========================================================
                    LEFT — TRADE INSTITUTE EXPERIENCE
                ========================================================= */}

                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                        position: "relative",
                        minHeight: "100dvh",
                        overflow: "hidden",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        px: {
                            md: 6,
                            lg: 8,
                            xl: 10,
                        },
                        py: 5,
                        color: COLORS.white,
                        background:
                            "linear-gradient(135deg, #041017 0%, #071923 45%, #0C3027 100%)",
                    }}
                >
                    <TechnicalGrid />

                    <Glow
                        size={650}
                        top={-350}
                        left={-300}
                        color="rgba(34,168,97,.20)"
                    />

                    <Glow
                        size={520}
                        bottom={-300}
                        right={-260}
                        color="rgba(229,169,26,.13)"
                    />

                    <FloatingTradeIcon
                        Icon={Engineering}
                        top="21%"
                        left="9%"
                        delay={0}
                        size={32}
                    />

                    <FloatingTradeIcon
                        Icon={Construction}
                        top="35%"
                        right="12%"
                        delay={1}
                        size={30}
                    />

                    <FloatingTradeIcon
                        Icon={ElectricalServices}
                        top="51%"
                        left="8%"
                        delay={2}
                        size={29}
                    />

                    <FloatingTradeIcon
                        Icon={Plumbing}
                        top="66%"
                        right="10%"
                        delay={3}
                        size={30}
                    />

                    <FloatingTradeIcon
                        Icon={PrecisionManufacturing}
                        top="76%"
                        left="20%"
                        delay={4}
                        size={28}
                    />

                    <FloatingTradeIcon
                        Icon={Carpenter}
                        top="42%"
                        left="42%"
                        delay={5}
                        size={27}
                    />

                    {/* HEADER */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "13px",
                                    background:
                                        "linear-gradient(135deg, #16834B, #22A861)",
                                    boxShadow:
                                        "0 12px 30px rgba(0,0,0,.3)",
                                }}
                            >
                                <Engineering
                                    sx={{
                                        fontSize: 28,
                                        color: COLORS.white,
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: ".7rem",
                                        fontWeight: 900,
                                        letterSpacing: 1.8,
                                        color: COLORS.brightGold,
                                    }}
                                >
                                    POLU
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: ".82rem",
                                        fontWeight: 800,
                                        letterSpacing: 1,
                                        color:
                                            "rgba(255,255,255,.82)",
                                    }}
                                >
                                    TRAINING MANAGEMENT SYSTEM
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.6,
                                py: .75,
                                borderRadius: "30px",
                                border:
                                    "1px solid rgba(255,255,255,.13)",
                                background:
                                    "rgba(255,255,255,.055)",
                                backdropFilter: "blur(12px)",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    background:
                                        COLORS.brightGreen,
                                    boxShadow:
                                        "0 0 12px rgba(34,168,97,.8)",
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".61rem",
                                    fontWeight: 900,
                                    letterSpacing: 1,
                                    color:
                                        "rgba(255,255,255,.75)",
                                }}
                            >
                                SKILLS ONLINE
                            </Typography>
                        </Box>
                    </Box>

                    {/* CENTER CONTENT */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            maxWidth: 730,
                            mt: -4,
                        }}
                    >
                        <Typography
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: .8,
                                px: 1.7,
                                py: .8,
                                mb: 2.5,
                                borderRadius: "6px",
                                background:
                                    "rgba(229,169,26,.12)",
                                border:
                                    "1px solid rgba(229,169,26,.30)",
                                color: COLORS.brightGold,
                                fontSize: ".63rem",
                                fontWeight: 950,
                                letterSpacing: 1.8,
                            }}
                        >
                            <Build sx={{ fontSize: 15 }} />
                            TRADE TRAINING INSTITUTE
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: {
                                    md: "3.8rem",
                                    lg: "4.8rem",
                                    xl: "5.5rem",
                                },
                                fontWeight: 1000,
                                lineHeight: .95,
                                letterSpacing: "-3px",
                                textTransform: "uppercase",
                            }}
                        >
                            Build
                            <br />
                            Your
                            <Box
                                component="span"
                                sx={{
                                    color: COLORS.brightGold,
                                    display: "block",
                                }}
                            >
                                Future.
                            </Box>
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 560,
                                mt: 3,
                                color:
                                    "rgba(255,255,255,.68)",
                                fontSize: "1rem",
                                lineHeight: 1.8,
                            }}
                        >
                            Develop practical skills, master
                            modern trades, and prepare yourself
                            for meaningful opportunities through
                            hands-on technical training.
                        </Typography>

                        {/* TRADE CATEGORIES */}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(2, minmax(0, 1fr))",
                                gap: 1.2,
                                maxWidth: 510,
                                mt: 3.5,
                            }}
                        >
                            {[
                                {
                                    icon: Engineering,
                                    label: "Engineering",
                                },
                                {
                                    icon: ElectricalServices,
                                    label: "Electrical",
                                },
                                {
                                    icon: Construction,
                                    label: "Construction",
                                },
                                {
                                    icon: Handyman,
                                    label: "Technical Skills",
                                },
                            ].map(
                                ({
                                    icon: Icon,
                                    label,
                                }) => (
                                    <Box
                                        key={label}
                                        sx={{
                                            display: "flex",
                                            alignItems:
                                                "center",
                                            gap: 1.2,
                                            px: 1.6,
                                            py: 1.25,
                                            borderRadius:
                                                "10px",
                                            background:
                                                "rgba(255,255,255,.045)",
                                            border:
                                                "1px solid rgba(255,255,255,.09)",
                                        }}
                                    >
                                        <Icon
                                            sx={{
                                                fontSize: 20,
                                                color:
                                                    COLORS.brightGold,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                fontSize:
                                                    ".75rem",
                                                fontWeight:
                                                    800,
                                                color:
                                                    "rgba(255,255,255,.8)",
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    </Box>

                    {/* BOTTOM */}

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                            borderTop:
                                "1px solid rgba(255,255,255,.10)",
                            pt: 2.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: ".65rem",
                                fontWeight: 800,
                                letterSpacing: 1.3,
                                color:
                                    "rgba(255,255,255,.45)",
                            }}
                        >
                            LEARN • PRACTICE • MASTER
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: ".65rem",
                                fontWeight: 800,
                                color:
                                    "rgba(255,255,255,.45)",
                            }}
                        >
                            SKILLS FOR ALL
                        </Typography>
                    </Box>
                </Box>

                {/* =========================================================
                    RIGHT — LOGIN
                ========================================================= */}

                <Box
                    sx={{
                        position: "relative",
                        minHeight: "100dvh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        px: {
                            xs: 2,
                            sm: 4,
                            md: 5,
                            lg: 7,
                        },
                        py: {
                            xs: 4,
                            md: 5,
                        },
                        background:
                            "linear-gradient(150deg, #FFFFFF 0%, #F6FAF8 100%)",
                    }}
                >
                    <Glow
                        size={400}
                        top={-230}
                        right={-220}
                        color="rgba(34,168,97,.10)"
                    />

                    <Glow
                        size={330}
                        bottom={-200}
                        left={-190}
                        color="rgba(229,169,26,.09)"
                    />

                    {/* MOBILE BRAND */}

                    <Box
                        sx={{
                            display: {
                                xs: "flex",
                                md: "none",
                            },
                            position: "absolute",
                            top: 18,
                            left: 18,
                            right: 18,
                            alignItems: "center",
                            justifyContent:
                                "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: .9,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "10px",
                                    background:
                                        COLORS.navy,
                                }}
                            >
                                <Engineering
                                    sx={{
                                        color: COLORS.brightGold,
                                        fontSize: 21,
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: ".68rem",
                                    fontWeight: 950,
                                    letterSpacing: 1,
                                    color: COLORS.navy,
                                }}
                            >
                                TRADE TRAINING
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                px: 1.2,
                                py: .55,
                                borderRadius: "20px",
                                background:
                                    COLORS.paleGreen,
                                color: COLORS.green,
                                fontSize: ".58rem",
                                fontWeight: 900,
                            }}
                        >
                            ONLINE
                        </Box>
                    </Box>

                    {/* LOGIN CONTAINER */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            x: 35,
                        }}
                        animate={{
                            opacity: 1,
                            x: 0,
                        }}
                        transition={{
                            duration: .7,
                            ease: "easeOut",
                        }}
                        style={{
                            position: "relative",
                            zIndex: 10,
                            width: "100%",
                            maxWidth: 455,
                        }}
                    >
                        {/* SMALL TOP BRAND */}

                        <Box
                            sx={{
                                textAlign: "center",
                                mb: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 68,
                                    height: 68,
                                    mx: "auto",
                                    mb: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "20px",
                                    background:
                                        "linear-gradient(135deg, #071923, #16834B)",
                                    boxShadow:
                                        "0 18px 35px rgba(7,25,35,.18)",
                                    position: "relative",
                                }}
                            >
                                <Engineering
                                    sx={{
                                        fontSize: 35,
                                        color: COLORS.white,
                                    }}
                                />

                                <Box
                                    sx={{
                                        position: "absolute",
                                        width: 13,
                                        height: 13,
                                        right: -4,
                                        top: -4,
                                        borderRadius: "50%",
                                        background:
                                            COLORS.brightGold,
                                        border:
                                            "3px solid #F7F8F5",
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: ".67rem",
                                    fontWeight: 950,
                                    letterSpacing: 2,
                                    color: COLORS.green,
                                }}
                            >
                                TRADE TRAINING INSTITUTE
                            </Typography>

                            <Typography
                                sx={{
                                    mt: .7,
                                    fontSize: {
                                        xs: "2rem",
                                        sm: "2.25rem",
                                    },
                                    fontWeight: 1000,
                                    letterSpacing: "-1.2px",
                                    color: COLORS.navy,
                                    lineHeight: 1.05,
                                }}
                            >
                                Welcome Back
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 1,
                                    color: COLORS.muted,
                                    fontSize: ".86rem",
                                    lineHeight: 1.6,
                                }}
                            >
                                Sign in to continue your
                                training journey.
                            </Typography>
                        </Box>

                        {/* LOGIN PANEL */}

                        <Box
                            sx={{
                                position: "relative",
                                p: {
                                    xs: 2.3,
                                    sm: 3.5,
                                },
                                borderRadius: "24px",
                                background:
                                    "rgba(255,255,255,.92)",
                                border:
                                    `1px solid ${COLORS.border}`,
                                boxShadow:
                                    "0 25px 70px rgba(7,25,35,.10)",
                                backdropFilter:
                                    "blur(18px)",
                            }}
                        >
                            {/* PANEL HEADER */}

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "space-between",
                                    mb: 2.5,
                                    pb: 2,
                                    borderBottom:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: ".78rem",
                                            fontWeight: 950,
                                            color:
                                                COLORS.navy,
                                        }}
                                    >
                                        MEMBER LOGIN
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: .35,
                                            fontSize: ".67rem",
                                            color:
                                                COLORS.muted,
                                        }}
                                    >
                                        Access your institute portal
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        gap: .6,
                                        px: 1,
                                        py: .6,
                                        borderRadius:
                                            "7px",
                                        background:
                                            COLORS.paleGreen,
                                    }}
                                >
                                    <VerifiedUser
                                        sx={{
                                            fontSize: 14,
                                            color:
                                                COLORS.green,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            fontSize:
                                                ".55rem",
                                            fontWeight:
                                                900,
                                            color:
                                                COLORS.green,
                                        }}
                                    >
                                        SECURE
                                    </Typography>
                                </Box>
                            </Box>

                            <form
                                onSubmit={
                                    handleSubmit
                                }
                            >
                                {/* EMAIL */}

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        sx={{
                                            mb: .7,
                                            ml: .2,
                                            fontSize: ".68rem",
                                            fontWeight: 900,
                                            color:
                                                COLORS.text,
                                        }}
                                    >
                                        EMAIL ADDRESS
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(
                                                e.target.value
                                            )
                                        }
                                        required
                                        autoComplete="email"
                                        InputProps={{
                                            startAdornment:
                                                (
                                                    <InputAdornment position="start">
                                                        <Email
                                                            sx={{
                                                                fontSize: 20,
                                                                color:
                                                                    COLORS.green,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root":
                                                {
                                                    minHeight: 56,
                                                    borderRadius:
                                                        "12px",
                                                    background:
                                                        "#FBFDFC",
                                                    "& fieldset":
                                                        {
                                                            borderColor:
                                                                COLORS.border,
                                                        },
                                                    "&:hover fieldset":
                                                        {
                                                            borderColor:
                                                                "#9CCDB2",
                                                        },
                                                    "&.Mui-focused fieldset":
                                                        {
                                                            borderColor:
                                                                COLORS.green,
                                                            borderWidth:
                                                                1.5,
                                                        },
                                                },
                                            "& input":
                                                {
                                                    fontSize:
                                                        ".88rem",
                                                },
                                        }}
                                    />
                                </Box>

                                {/* PASSWORD */}

                                <Box sx={{ mb: 1 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems:
                                                "center",
                                            mb: .7,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                ml: .2,
                                                fontSize:
                                                    ".68rem",
                                                fontWeight:
                                                    900,
                                                color:
                                                    COLORS.text,
                                            }}
                                        >
                                            PASSWORD
                                        </Typography>

                                        <Link
                                            to="/forgotPassword"
                                            style={{
                                                color:
                                                    COLORS.green,
                                                fontSize:
                                                    ".7rem",
                                                fontWeight:
                                                    850,
                                                textDecoration:
                                                    "none",
                                            }}
                                        >
                                            Forgot password?
                                        </Link>
                                    </Box>

                                    <TextField
                                        fullWidth
                                        placeholder="Enter your password"
                                        type={
                                            showPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(
                                                e.target.value
                                            )
                                        }
                                        required
                                        autoComplete="current-password"
                                        InputProps={{
                                            startAdornment:
                                                (
                                                    <InputAdornment position="start">
                                                        <Lock
                                                            sx={{
                                                                fontSize: 20,
                                                                color:
                                                                    COLORS.green,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            endAdornment:
                                                (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() =>
                                                                setShowPassword(
                                                                    (
                                                                        prev
                                                                    ) =>
                                                                        !prev
                                                                )
                                                            }
                                                            edge="end"
                                                            aria-label={
                                                                showPassword
                                                                    ? "Hide password"
                                                                    : "Show password"
                                                            }
                                                        >
                                                            {showPassword ? (
                                                                <VisibilityOff
                                                                    sx={{
                                                                        fontSize: 20,
                                                                        color:
                                                                            COLORS.muted,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Visibility
                                                                    sx={{
                                                                        fontSize: 20,
                                                                        color:
                                                                            COLORS.muted,
                                                                    }}
                                                                />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root":
                                                {
                                                    minHeight: 56,
                                                    borderRadius:
                                                        "12px",
                                                    background:
                                                        "#FBFDFC",
                                                    "& fieldset":
                                                        {
                                                            borderColor:
                                                                COLORS.border,
                                                        },
                                                    "&:hover fieldset":
                                                        {
                                                            borderColor:
                                                                "#9CCDB2",
                                                        },
                                                    "&.Mui-focused fieldset":
                                                        {
                                                            borderColor:
                                                                COLORS.green,
                                                            borderWidth:
                                                                1.5,
                                                        },
                                                },
                                            "& input":
                                                {
                                                    fontSize:
                                                        ".88rem",
                                                },
                                        }}
                                    />
                                </Box>

                                {/* LOGIN BUTTON */}

                                <Button
                                    type="submit"
                                    fullWidth
                                    disabled={loading}
                                    sx={{
                                        mt: 2.4,
                                        minHeight: 57,
                                        borderRadius: "12px",
                                        textTransform:
                                            "none",
                                        fontSize: ".9rem",
                                        fontWeight: 950,
                                        color: COLORS.white,
                                        background:
                                            `linear-gradient(
                                                135deg,
                                                ${COLORS.navy} 0%,
                                                ${COLORS.steel} 48%,
                                                ${COLORS.green} 100%
                                            )`,
                                        boxShadow:
                                            "0 14px 28px rgba(7,25,35,.18)",
                                        "&:hover":
                                            {
                                                background:
                                                    `linear-gradient(
                                                        135deg,
                                                        ${COLORS.steel},
                                                        ${COLORS.green}
                                                    )`,
                                                transform:
                                                    "translateY(-1px)",
                                                boxShadow:
                                                    "0 18px 34px rgba(7,25,35,.22)",
                                            },
                                        "&:disabled":
                                            {
                                                color:
                                                    COLORS.white,
                                                background:
                                                    "#93A39A",
                                            },
                                        transition:
                                            "all .25s ease",
                                    }}
                                >
                                    {loading ? (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                gap: 1.2,
                                            }}
                                        >
                                            <CircularProgress
                                                size={20}
                                                thickness={5}
                                                sx={{
                                                    color:
                                                        COLORS.white,
                                                }}
                                            />
                                            Authenticating...
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                gap: .9,
                                            }}
                                        >
                                            Enter Training Portal
                                            <ArrowForward
                                                sx={{
                                                    fontSize: 18,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Button>

                                {/* HOME */}

                                <Button
                                    fullWidth
                                    variant="text"
                                    onClick={() =>
                                        navigate("/")
                                    }
                                    startIcon={
                                        <ArrowBack />
                                    }
                                    sx={{
                                        mt: 1.2,
                                        color:
                                            COLORS.muted,
                                        fontSize: ".78rem",
                                        fontWeight: 800,
                                        textTransform:
                                            "none",
                                        "&:hover":
                                            {
                                                background:
                                                    COLORS.paleGreen,
                                                color:
                                                    COLORS.navy,
                                            },
                                    }}
                                >
                                    Return to Institute Home
                                </Button>
                            </form>
                        </Box>

                        {/* FOOTER */}

                        <Box
                            sx={{
                                mt: 2.5,
                                textAlign: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        "center",
                                    alignItems: "center",
                                    gap: .7,
                                    mb: .7,
                                }}
                            >
                                <School
                                    sx={{
                                        fontSize: 14,
                                        color:
                                            COLORS.green,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontSize: ".61rem",
                                        fontWeight: 900,
                                        letterSpacing: .7,
                                        color:
                                            COLORS.green,
                                    }}
                                >
                                    PROFESSIONAL SKILLS • PRACTICAL TRAINING
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: ".62rem",
                                    color:
                                        COLORS.muted,
                                }}
                            >
                                Your credentials are protected
                                by secure authentication.
                            </Typography>
                        </Box>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;

